import { useInfiniteQuery } from '@zg-rentals/react-query5';
import { RentalManagerAccountViewZrmListingsWebApiHandler_list } from '@zg-rentals/ts-rental-manager-api-client';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import type { ListingUnitDto, ListingUnitsResponseDto } from '@zg-rentals/ts-rental-manager-api-client';

export const LISTING_UNITS_LIST_PAGE_LIMIT = PropertiesConstants.PROPERTIES_MULTIPLEX_LIMIT_DEFAULT;

export type ListingUnitsListParams = {
  searchText?: string;
};

export type UseGetListingUnitsListOptions = {
  enabled?: boolean;
};

export const useGetListingUnitsList = (
  { searchText = '' }: ListingUnitsListParams,
  { enabled = true }: UseGetListingUnitsListOptions = {},
) => {
  const { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isError } = useInfiniteQuery<
    ListingUnitsResponseDto,
    Error,
    { pages: Array<ListingUnitsResponseDto> },
    Array<string | undefined>,
    string
  >({
    queryKey: ['getListingUnitsList', searchText],
    queryFn: async ({ pageParam }) =>
      RentalManagerAccountViewZrmListingsWebApiHandler_list({
        limit: LISTING_UNITS_LIST_PAGE_LIMIT,
        ...(searchText ? { searchText } : {}),
        ...(pageParam ? { startKeyExclusive: pageParam } : {}),
      }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
    enabled,
    // Hold the previous page set while a new search resolves so the table doesn't flash empty.
    placeholderData: (previousData) => (previousData?.pages?.length ? previousData : undefined),
    meta: {
      tags: ['getListingUnitsList'],
      context: {
        api: 'RentalManagerAccountViewZrmListingsWebApiHandler_list',
        details: 'useGetListingUnitsList',
      },
    },
  });

  const units: Array<ListingUnitDto> = data?.pages.flatMap((page) => page.units) ?? [];
  const totalCount = data?.pages[0]?.totalCount;

  return { units, totalCount, error, isError, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage };
};
